import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { useKeyedState } from '#/hooks/useKeyedState';
import { isClientHydrated } from '#/rendering/state/renderingState';
import { scrollToTop } from '#/utils/scroll';

import {
  isReloadNavigation,
  parseStepSearchParam,
  removeStepSearchParam,
} from './multiStepSearchParams';

export const useMultiStep = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isClientNavigation] = useKeyedState(isClientHydrated(), {
    key: location.key,
  });

  const currentStep = useMemo(
    () => parseStepSearchParam(searchParams.get('step')),
    [searchParams],
  );

  useEffect(() => {
    if (currentStep === 0 || !isReloadNavigation() || isClientNavigation) {
      return;
    }

    setSearchParams((params) => removeStepSearchParam(params), {
      replace: true,
      preventScrollReset: true,
    });
  }, [currentStep, isClientNavigation, setSearchParams]);

  const navigateToStep = useCallback(
    (step: number | ((prevStep: number) => number)) => {
      scrollToTop();
      window.setTimeout(() => {
        const newStep = typeof step === 'function' ? step(currentStep) : step;
        setSearchParams({ step: newStep.toString() });
      }, 10);
    },
    [setSearchParams, currentStep],
  );

  return { navigateToStep, currentStep };
};
